import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, OnDestroy, inject, signal } from '@angular/core';
import { Observable, Subscription, catchError, interval, of, startWith, switchMap } from 'rxjs';
import {
  AppNotification,
  NotificationListResponse,
  NotificationPreferencesResponse,
  NotificationPreferencesUpdatePayload,
  NotificationUnreadCountResponse,
  PushPublicKeyResponse
} from '../models/notification.models';

export interface ListNotificationsOptions {
  page?: number;
  perPage?: number;
  unreadOnly?: boolean;
}

/**
 * The member's notification centre (`/api/notifications/*`): the header bell
 * (this service's own `unreadCount` signal, polled every 60s while a
 * consumer holds it open — same reference-counted `start()`/`stop()` pattern
 * as CommunityUnreadService) plus list/read/preferences/push-subscription
 * calls used by the notifications and settings pages.
 *
 * Lives in `app`, not `community`, so the layout's header bell (which must
 * not import `community`) can use it directly.
 */
@Injectable({ providedIn: 'root' })
export class NotificationsService implements OnDestroy {
  private http = inject(HttpClient);

  private static readonly POLL_INTERVAL_MS = 60_000;
  private readonly base = '/notifications';

  /** Unread notification count, kept fresh while `start()` has at least one holder. */
  readonly unreadCount = signal(0);

  private holders = 0;
  private polling: Subscription | null = null;

  /** Seed the badge from the bootstrap/login payload, before the first poll answers. */
  seed(count: number | null | undefined): void {
    this.unreadCount.set(count ?? 0);
  }

  start(): void {
    this.holders += 1;
    if (this.polling) return;
    this.polling = interval(NotificationsService.POLL_INTERVAL_MS)
      .pipe(
        startWith(0),
        switchMap(() => this.unreadCountRequest().pipe(catchError(() => of(null))))
      )
      .subscribe(res => {
        if (res) this.unreadCount.set(res.unread_count);
      });
  }

  stop(): void {
    this.holders = Math.max(0, this.holders - 1);
    if (this.holders > 0) return;
    this.polling?.unsubscribe();
    this.polling = null;
  }

  refresh(): void {
    this.unreadCountRequest().pipe(catchError(() => of(null))).subscribe(res => {
      if (res) this.unreadCount.set(res.unread_count);
    });
  }

  ngOnDestroy(): void {
    this.polling?.unsubscribe();
  }

  // --- Listing & read state ---------------------------------------------------

  private unreadCountRequest(): Observable<NotificationUnreadCountResponse> {
    return this.http.get<NotificationUnreadCountResponse>(`${this.base}/unread_count`);
  }

  list(options: ListNotificationsOptions = {}): Observable<NotificationListResponse> {
    let params = new HttpParams()
      .set('page', String(options.page ?? 1))
      .set('per_page', String(options.perPage ?? 20));
    if (options.unreadOnly) params = params.set('unread', '1');
    return this.http.get<NotificationListResponse>(this.base, { params });
  }

  markRead(id: number): Observable<{ notification: AppNotification }> {
    return this.http.post<{ notification: AppNotification }>(`${this.base}/${id}/read`, {});
  }

  markAllRead(): Observable<{ ok: boolean; unread_count: number }> {
    return this.http.post<{ ok: boolean; unread_count: number }>(`${this.base}/read_all`, {});
  }

  // --- Preferences --------------------------------------------------------------

  getPreferences(): Observable<NotificationPreferencesResponse> {
    return this.http.get<NotificationPreferencesResponse>(`${this.base}/preferences`);
  }

  updatePreferences(payload: NotificationPreferencesUpdatePayload): Observable<{ preferences: NotificationPreferencesResponse['preferences'] }> {
    return this.http.put<{ preferences: NotificationPreferencesResponse['preferences'] }>(`${this.base}/preferences`, payload);
  }

  // --- Web push ------------------------------------------------------------------

  getPushPublicKey(): Observable<PushPublicKeyResponse> {
    return this.http.get<PushPublicKeyResponse>(`${this.base}/push_public_key`);
  }

  createPushSubscription(subscription: PushSubscriptionJSON): Observable<{ id: number }> {
    return this.http.post<{ id: number }>(`${this.base}/push_subscriptions`, { subscription });
  }

  deletePushSubscription(endpoint: string): Observable<{ ok: boolean }> {
    return this.http.request<{ ok: boolean }>('DELETE', `${this.base}/push_subscriptions`, { body: { endpoint } });
  }
}
